/**
 * The one transport for Forge api-dl endpoints: auth header, error-envelope
 * parsing, optional JSON body, optional AbortSignal, and in-flight request
 * coalescing live here so endpoint modules stay one-line descriptors.
 *
 * Wire casing follows ADR-0021: bodies and query strings are serialized
 * verbatim, so callers keep providing snake_case request shapes.
 */
import { ApiService } from '../../services/api';

export class ForgeApiError extends Error {
  status: number;

  detail?: string;

  rawBody?: string;

  constructor(message: string, status: number, detail?: string, rawBody?: string) {
    super(message);
    this.name = 'ForgeApiError';
    this.status = status;
    this.detail = detail;
    this.rawBody = rawBody;
  }
}

type ApiErrorPayload = {
  detail?: string | { code?: string; message?: string };
  message?: string;
  errors?: Array<{ detail?: string | { code?: string; message?: string }; message?: string }>;
};

function normalizeApiErrorDetail(detail: ApiErrorPayload['detail']): string | undefined {
  if (!detail) return undefined;
  if (typeof detail === 'string') {
    try {
      const parsedDetail = JSON.parse(detail) as ApiErrorPayload['detail'];
      if (parsedDetail && typeof parsedDetail !== 'string') {
        return parsedDetail.message || parsedDetail.code || detail;
      }
    } catch {
      return detail;
    }
    return detail;
  }
  return detail.message || detail.code;
}

export async function parseApiError(response: Response, fallback: string): Promise<ForgeApiError> {
  const rawBody = await response.text().catch(() => '');
  let payload: ApiErrorPayload | null = null;

  if (rawBody) {
    try {
      payload = JSON.parse(rawBody) as ApiErrorPayload;
    } catch {
      payload = null;
    }
  }

  const nestedError = payload?.errors?.find((error) => error.detail || error.message);
  const detail =
    normalizeApiErrorDetail(payload?.detail) ||
    payload?.message ||
    normalizeApiErrorDetail(nestedError?.detail) ||
    nestedError?.message ||
    rawBody ||
    undefined;
  const message = detail || `${fallback} (${response.status} ${response.statusText})`;

  return new ForgeApiError(message, response.status, detail, rawBody || undefined);
}

function filenameFromContentDisposition(value: string | null, fallback: string): string {
  if (!value) return fallback;
  const filenameStarMatch = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (filenameStarMatch?.[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1]);
    } catch {
      return fallback;
    }
  }
  const filenameMatch = value.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] ?? fallback;
}

export type ForgeRequestOptions = {
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON body, serialized verbatim (snake_case per ADR-0021 stays the caller's contract). */
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Coalesce concurrent identical requests: while a request with this
   * `[endpointName, key]` pair is in flight, later callers receive the same
   * promise. The endpoint-name segment structurally segregates the registry so
   * two endpoints can never share a key. Ignored when `signal` is set: a
   * shared promise cannot honor per-caller aborts, so abortable requests
   * always run standalone.
   */
  dedupeKey?: readonly [endpointName: string, key: string];
};

const inFlightRequests = new Map<string, Promise<unknown>>();

/** Fetch with the Forge auth/error contract and hand back the raw Response. */
export async function forgeFetch(
  token: string,
  url: string,
  errorLabel: string,
  options: ForgeRequestOptions = {}
): Promise<Response> {
  const { method, body, signal } = options;
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const response = await fetch(url, {
    ...(method ? { method } : {}),
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  });
  ApiService.handleError(response);
  if (!response.ok) {
    throw await parseApiError(response, errorLabel);
  }
  return response;
}

export async function forgeRequest<T>(
  token: string,
  url: string,
  errorLabel: string,
  options: ForgeRequestOptions = {}
): Promise<T> {
  const { dedupeKey } = options;
  if (dedupeKey !== undefined && !options.signal) {
    const registryKey = dedupeKey.join(' ');
    const existing = inFlightRequests.get(registryKey);
    if (existing) return existing as Promise<T>;
    const request = forgeFetch(token, url, errorLabel, options).then(
      (response) => response.json() as Promise<T>
    );
    inFlightRequests.set(registryKey, request);
    try {
      return await request;
    } finally {
      inFlightRequests.delete(registryKey);
    }
  }
  const response = await forgeFetch(token, url, errorLabel, options);
  return response.json();
}

export async function forgeVoidRequest(
  token: string,
  url: string,
  errorLabel: string,
  options: ForgeRequestOptions = {}
): Promise<void> {
  await forgeFetch(token, url, errorLabel, options);
}

export async function forgeBlobRequest(
  token: string,
  url: string,
  errorLabel: string,
  fallbackFilename: string,
  options: ForgeRequestOptions = {}
): Promise<{ blob: Blob; filename: string }> {
  const response = await forgeFetch(token, url, errorLabel, options);
  const blob = await response.blob();
  const filename = filenameFromContentDisposition(
    response.headers.get('content-disposition'),
    fallbackFilename
  );
  return { blob, filename };
}
